import FormControlLabel from '@mui/material/FormControlLabel';
import * as _ from 'lodash';
import type { FormControlLabelProps } from '@mui/material/FormControlLabel';
import type { ChangeEvent, FunctionComponent, ReactNode } from 'react';

import { icons } from '../Icon';
import TooltipIcon from '../TooltipIcon';
import type { TooltipPlacement } from '../Tooltip';

import Checkbox from './Checkbox';

import css from './LabeledCheckbox.module.less';

type LabeledCheckboxProps = {
  label: ReactNode;
  id?: string;
  isChecked: boolean;
  isDisabled?: boolean;
  labelPlacement?: FormControlLabelProps['labelPlacement'];
  onChange?: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  testId?: string;
  //  If the MUI ripples should be disabled or not
  isRippleDisabled?: boolean;
  tooltip?: string | ReactNode;
  tooltipPlacement?: TooltipPlacement;
  className?: string;
};

/**
 * Wrapper for MaterialUI's <FormControlLabel /> + <Checkbox />
 * More aligned with the old Checkbox component API
 */
const LabeledCheckbox: FunctionComponent<LabeledCheckboxProps> = ({
  label,
  id,
  isChecked,
  isDisabled,
  labelPlacement = 'end',
  onChange = _.noop,
  testId,
  isRippleDisabled,
  tooltip,
  tooltipPlacement,
  className,
}) => {
  /**
   * Helper for creating checkbox filter labels with optional tooltips.
   */
  const createCheckboxLabel = (
    labelText: string | ReactNode,
    checkboxId?: string,
    tooltipText?: string | ReactNode,
  ): ReactNode => {
    if (!tooltipText) {
      return labelText;
    }
    return (
      <span className={css.labelWithTooltip}>
        <span>{labelText}</span>
        {tooltipText && (
          <span className={css.checkboxLabelTooltips}>
            <TooltipIcon
              id={`tooltip-${checkboxId || ''}`}
              icon={icons.questionCircleO}
              tooltip={tooltipText}
              placement={tooltipPlacement}
              testId={`tooltip-${checkboxId || ''}`}
            />
          </span>
        )}
      </span>
    );
  };

  return (
    <FormControlLabel
      label={createCheckboxLabel(label, id, tooltip)}
      labelPlacement={labelPlacement}
      className={className}
      control={
        <Checkbox
          id={id}
          isChecked={isChecked}
          onChange={onChange}
          testId={testId}
          isRippleDisabled={isRippleDisabled}
          isDisabled={isDisabled}
        />
      }
      disabled={!!isDisabled}
    />
  );
};

export default LabeledCheckbox;
